// ============================================================
// NativeEngine — iOS/Android lightweight engine
// Uses native <video> HLS support, no hls.js
// ============================================================
import type { StreamSource, QualityLevel } from '../core/types';
import type { EngineContext, IEngine } from './IEngine';

export class NativeEngine implements IEngine {
  readonly name = 'native';
  readonly isLowLatency = false;

  private video: HTMLVideoElement | null = null;
  private container: HTMLElement | null = null;
  private source: StreamSource | null = null;

  private readonly eventBus;
  private readonly store;
  private readonly logger;
  private programmaticVolumeChange = 0;

  constructor(ctx: EngineContext) {
    this.eventBus = ctx.eventBus;
    this.store = ctx.store;
    this.logger = ctx.logger;
  }

  attach(container: HTMLElement): void {
    this.container = container;
    this.video = document.createElement('video');
    this.video.setAttribute('playsinline', 'playsinline');
    this.video.setAttribute('webkit-playsinline', 'webkit-playsinline');
    this.video.style.width = '100%';
    this.video.style.height = '100%';
    this.video.setAttribute('loop', 'true');
    container.appendChild(this.video);
    this.bindVideoEvents();
  }

  load(source: StreamSource, queryParams: string): void {
    if (!this.video) return;
    this.source = source;
    // Native — prefer explicit mp4, then master/source
    const rawUrl = source.mp4 || source.master || source.source || '';
    if (!rawUrl) {
      this.logger.error('No stream URL');
      return;
    }

    this.video.src = rawUrl;
    this.eventBus.emit('engine:ready');
  }

  play(): void {
    if (!this.video) return;
    this.eventBus.emit('playback:requested');
    this.video.play().catch((e) => {
      this.logger.warn('Autoplay blocked:', e.message);
      this.video!.muted = true;
      this.store.setState({ isMuted: true });
      return this.video!.play().catch(() => this.eventBus.emit('playback:blocked'));
    });
  }

  pause(): void {
    this.eventBus.emit('playback:cancelled');
    this.video?.pause();
  }

  stop(): void {
    if (this.video) {
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
    }
    this.store.setState({ isPlaying: false });
    this.eventBus.emit('stopped');
  }

  destroy(): void {
    this.stop();
    if (this.video) {
      this.video.remove();
      this.video = null;
    }
    this.eventBus.emit('engine:destroyed');
  }

  setQuality(level: QualityLevel): void {
    // Native HLS: switch quality via src-switching if per-quality URLs available
    if (!this.video || !this.source?.qualities) return;
    const url = this.source.qualities[level];
    if (url) {
      const wasPlaying = !this.video.paused;
      this.video.src = url;
      if (wasPlaying) this.video.play();
      this.store.setState({ quality: level });
      this.eventBus.emit('quality:changed', level);
    }
  }

  getQualities(): QualityLevel[] {
    // Native HLS doesn't expose quality levels
    if (!this.source?.qualities) return [];
    return Object.keys(this.source.qualities) as QualityLevel[];
  }

  setVolume(value: number): void {
    const rounded = Math.round(value * 100) / 100;
    if (this.video) {
      this.programmaticVolumeChange += 2; // volume + muted assignments
      this.video.volume = rounded;
      this.video.muted = rounded === 0;
    }
    this.store.setState({ volume: rounded, isMuted: rounded === 0 });
  }

  seek(offset: number): void {
    if (this.video) {
      this.video.currentTime = Math.max(0, this.video.currentTime + offset);
    }
  }

  getLatency(): number { return 0; }

  getBufferLength(): number {
    if (!this.video || !this.video.buffered.length) return 0;
    for (let i = 0; i < this.video.buffered.length; i++) {
      if (
        this.video.currentTime >= this.video.buffered.start(i) &&
        this.video.currentTime <= this.video.buffered.end(i)
      ) {
        return this.video.buffered.end(i) - this.video.currentTime;
      }
    }
    return 0;
  }

  getBandwidth(): number { return 0; }

  getVideoElement(): HTMLVideoElement | null { return this.video; }

  // --- Private ---

  private bindVideoEvents(): void {
    const video = this.video!;

    video.addEventListener('playing', () => {
      this.store.setState({ isPlaying: true, loading: false });
      this.eventBus.emit('playing');
    });

    video.addEventListener('pause', () => {
      this.store.setState({ isPlaying: false });
    });

    video.addEventListener('waiting', () => {
      this.store.setState({ loading: true });
    });

    video.addEventListener('timeupdate', () => {
      this.store.setState({ currentTime: video.currentTime });
      this.eventBus.emit('time:update', video.currentTime);
    });

    video.addEventListener('loadedmetadata', () => {
      this.updateNativeDuration();
    });

    video.addEventListener('durationchange', () => {
      this.updateNativeDuration();
    });

    video.addEventListener('ended', () => {
      this.store.setState({ isPlaying: false });
      this.eventBus.emit('ended');
    });

    video.addEventListener('error', () => {
      const e = video.error;
      this.eventBus.emit('error', {
        code: e?.code ?? 0,
        message: e?.message ?? 'Unknown video error',
        fatal: true,
      });
    });

    let lastMuted = video.muted;
    video.addEventListener('volumechange', () => {
      const vol = Math.round(video.volume * 100) / 100;
      if (this.programmaticVolumeChange > 0) {
        this.programmaticVolumeChange--;
        this.store.setState({ volume: vol, isMuted: video.muted || vol === 0 });
        lastMuted = video.muted;
        return;
      }
      // Native/unsolicited change (right-click mute, OS media keys)
      this.store.setState({ volume: vol, isMuted: video.muted || vol === 0 });
      this.eventBus.emit('volume:change', vol);
      if (video.muted !== lastMuted) {
        lastMuted = video.muted;
        this.eventBus.emit('muted:change', video.muted);
      }
    });
  }

  /**
   * Native playback can only observe duration, never manifest liveness:
   * finite duration > 0 → VOD, Infinity (live/DVR window on Safari) → live,
   * otherwise leave streamType unchanged (metadata not usable yet).
   * isVod is derived by Store — races against engines are impossible.
   */
  private updateNativeDuration(): void {
    const raw = this.video?.duration;
    if (typeof raw !== 'number' || !isFinite(raw)) {
      this.store.setState({ streamType: 'live' });
      return;
    }
    const streamType = raw > 0 ? 'vod' as const : 'unknown' as const;
    this.store.setState({ duration: raw, streamType });
  }
}
